import { getRouteApi } from "@tanstack/react-router";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { getLocation, locations } from "@/data/locations";
import { SERVICES } from "@/data/services";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { LocationDeepLinks } from "@/components/LocationDeepLinks";
import { EntityPanel } from "@/components/EntityPanel";

const routeApi = getRouteApi("/en/locations/$slug");

export function EnLocationPage() {
  const { loc } = routeApi.useLoaderData();
  const related = locations
    .filter((l) => l.region === loc.region && l.slug !== loc.slug)
    .slice(0, 9);
  const services = Object.values(SERVICES);

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <Link
            to="/en/locations"
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-amber-300/80 hover:text-amber-300"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All locations
          </Link>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-300">
            <MapPin className="h-3 w-3" />
            {loc.type === "headquarters"
              ? "Head Office"
              : loc.type === "branch"
                ? "Branch"
                : loc.type === "bkk-district"
                  ? "Bangkok district"
                  : loc.type === "transit"
                    ? "Near BTS/MRT"
                    : loc.type === "mall"
                      ? "Mall meeting point"
                      : loc.type === "expat-hub"
                        ? "Expat hub"
                        : "Provincial agent"}
          </div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{loc.nameEn}</h1>
          <p className="mt-1 text-sm text-white/80">
            {loc.nameTh} · {loc.region}
          </p>

          {loc.description && (
            <p className="mt-5 max-w-3xl text-base text-white/80">
              Legal, notarisation and certified translation for residents, expats and businesses in{" "}
              {loc.nameEn}. Same-day pickup, MFA legalisation and worldwide DHL/FedEx delivery.
            </p>
          )}

          {loc.address && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/85">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
                <div>
                  <div className="font-semibold">Address</div>
                  <div>
                    {loc.addressEn ?? loc.address}
                    {loc.postalCode ? ` ${loc.postalCode}` : ""}
                  </div>
                  {loc.landmarksEn?.length ? (
                    <div className="mt-2 text-white/75">
                      <span className="font-semibold text-amber-300">Landmarks:</span>{" "}
                      {loc.landmarksEn.join(" · ")}
                    </div>
                  ) : null}
                  {loc.transitEn ? (
                    <div className="mt-1 text-white/75">
                      <span className="font-semibold text-amber-300">Getting here:</span>{" "}
                      {loc.transitEn}
                    </div>
                  ) : null}
                  {loc.mapUrl ? (
                    <a
                      href={loc.mapUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-amber-300 hover:underline"
                    >
                      Open in Google Maps →
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            <a
              href="tel:+66832494999"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-xs font-bold text-navy-deep hover:bg-amber-400"
            >
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white hover:bg-white/15"
            >
              <MessageCircle className="h-4 w-4 text-amber-300" /> LINE @NYCLI
            </a>
            <a
              href="mailto:contact@nyclegal.co.th"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white hover:bg-white/15"
            >
              <Mail className="h-4 w-4 text-amber-300" /> Email
            </a>
            <Link
              to="/locations/$slug"
              params={{ slug: loc.slug }}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-xs font-bold text-white/70 hover:text-amber-300"
            >
              ภาษาไทย
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold">Services available in {loc.nameEn}</h2>
          <p className="mt-2 text-sm text-white/70">
            Tap any service for pricing, FAQs and a step-by-step walkthrough specific to{" "}
            {loc.nameEn}.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {services.map((s) => (
              <li key={s.id}>
                <Link
                  to="/en/services/$service/$location"
                  params={{ service: s.slug, location: loc.slug }}
                  className="group flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-amber-400/40 hover:bg-white/[0.08]"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-amber-300" />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-white">{s.nameEn}</div>
                    <div className="mt-0.5 text-xs text-white/80">{s.taglineEn}</div>
                    <div className="mt-1 text-[11px] text-amber-300/80">
                      From ฿{s.priceFrom.toLocaleString()} · {s.turnaround}
                    </div>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 flex-none text-white/40 transition group-hover:translate-x-0.5 group-hover:text-amber-300" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <EntityPanel slug={loc.slug} locationName={loc.nameEn} locale="en" variant="dark" />

      <LocationDeepLinks locationSlug={loc.slug} locale="en" />

      {related.length > 0 && (
        <section className="border-t border-white/10 px-4 py-14">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-xl font-bold text-amber-300 sm:text-2xl">Nearby · {loc.region}</h2>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((l) => (
                <li key={l.slug}>
                  <Link
                    to="/en/locations/$slug"
                    params={{ slug: l.slug }}
                    className="block rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-medium text-white/85 transition hover:border-amber-400/40 hover:bg-white/[0.06]"
                  >
                    {l.nameEn}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </main>
  );
}
